// 🔧 register.js
// ✅ Purpose: Public registration page for new voters
// ✅ Features:
// - Separate firstName and lastName fields (required)
// - Email (required), Password (required), Phone (optional)
// - Detects QR flow (cid) and associates user to correct event
// - If no event exists yet, assigns one after voting
// - Sends email verification and continues to the verification dashboard
// - Stores user in /users/{uid} and defers event assignment
// - Preserves cid via sessionStorage for voting continuation
// - Shows saved vote preview from Firestore + sessionStorage above form

import React, { useEffect, useRef, useState } from "react";
import { auth, db } from "../../firebase";
import {
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { logger } from '../../utils/logger';
import { showStatus } from "../../utils/status_helpers";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import {
  SESSION_KEYS,
  getSessionValue,
  removeSessionValue,
} from "../../utils/browser_storage_helpers";
import SeoHead from "../../components/seo_head";
import { PUBLIC_PAGE_SEO } from "../../constants/marketing_pages";
import { getFunctions, httpsCallable } from "firebase/functions";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

import { completeEmailRegistration } from "../../services/email_registration";

function Register() {
  const registrationUserRef = useRef(null);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isVote = !!getSessionValue(
    SESSION_KEYS.PENDING_VOTE_CID
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState(null);
  const [pendingVote, setPendingVote] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const cid = searchParams.get("cid");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [mailingConsent, setMailingConsent] = useState(false);

  const getFriendlyErrorMessage = (code) => {
    switch (code) {
      case "auth/email-already-in-use":
        return "This email is already in use. Try logging in instead.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/weak-password":
        return "Password must be at least 6 characters.";
      case "auth/missing-password":
        return "Please enter a password.";
      default:
        return "Something went wrong. Please try again.";
    }
  };

  // ✅ Determine if registration form is valid
  const isFormValid =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    password.trim().length >= 6 &&
    agreedToTerms;

    const requiredFieldsCompleteExceptTerms =
      firstName.trim() &&
      lastName.trim() &&
      email.trim() &&
      password.trim().length >= 6 &&
      !agreedToTerms;
  
      useEffect(() => {
        const emailParam = searchParams.get("email");

        if (emailParam) {
          setEmail(emailParam);
        }
      }, [searchParams]);

  // ✅ Load vote item from Firestore using cid + eventSlug
  useEffect(() => {

    const pendingCid = getSessionValue(
      SESSION_KEYS.PENDING_VOTE_CID
    );

    const eventSlug = getSessionValue(
      SESSION_KEYS.LAST_EVENT_SLUG
    );

    if (!pendingCid || !eventSlug) {
      logger.log("ℹ️ Missing pendingVoteCid or eventSlug in sessionStorage", {});
      return;
    }

    const fetchCar = async () => {
      const pendingCid = getSessionValue(
        SESSION_KEYS.PENDING_VOTE_CID
      );

      const eventSlug = getSessionValue(
        SESSION_KEYS.LAST_EVENT_SLUG
      );

      logger.log("🧾 Loaded pendingVote:", { value: pendingVote });
      logger.log("📦 Loaded categoryName:", { value: categoryName });

      if (!pendingCid || !eventSlug) {
        logger.log("ℹ️ Missing pendingVoteCid or eventSlug in sessionStorage", {});
        return;
      }
    
      try {
        // ✅ Manually load slug → eventId map
        const resolvedEventId = getSessionValue(
          SESSION_KEYS.LAST_EVENT_ID
        );

        if (!resolvedEventId) {
          logger.warn("❌ Missing LAST_EVENT_ID in session storage", {});
          return;
        }
        const carRef = doc(db, "events", resolvedEventId, "cars", pendingCid);
        const snap = await getDoc(carRef);
    
        if (!snap.exists()) {
          logger.warn("❌ Car not found:", {
            value: pendingCid
          });
          return;
        }
    
        const carData = snap.data();
        setPendingVote(carData);

        if (!carData.description) {
          logger.warn("⚠️ Missing description in pendingVote", { value: carData });
        }
        
        if (carData.catid) {
          const catRef = doc(db, "events", resolvedEventId, "categories", String(carData.catid));
          const catSnap = await getDoc(catRef);
          if (catSnap.exists()) {
            const catData = catSnap.data();
            setCategoryName(catData.name || "Unknown");
          }
        }
      } catch (err) {
        logger.error("❗ Failed to load pending vote data:", {
          value: err
        });
      }
    };
      
    fetchCar();
  }, []);

  const linkPendingUserToUser = async ({ email }) => {
    const functions = getFunctions();
    const linkPendingUserToUserCallable = httpsCallable(
      functions,
      "linkPendingUserToUser"
    );

    const result = await linkPendingUserToUserCallable({
      email,
    });

    logger.log("✅ Pending user link function result:", {
      value: result.data,
    });

    return result.data;
  };

  // =====================================================
  // ✅ Facebook Registration / Login
  // =====================================================
  const handleFacebookRegister = async () => {
    const provider = new FacebookAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      logger.log("📘 Facebook login success", {
        uid: user.uid,
        email: user.email,
      });

      // ✅ Create or update user record
      await setDoc(
        doc(db, "users", user.uid),
        {
          uid: user.uid,
          email: user.email || "",
          firstName:
            user.displayName?.split(" ")[0] || "",
          lastName:
            user.displayName?.split(" ").slice(1).join(" ") || "",
          role: "voter",
          mailingConsent,
          provider: "facebook",
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );

      const pendingCid = getSessionValue(
        SESSION_KEYS.PENDING_VOTE_CID
      );

      const eventSlug = getSessionValue(
        SESSION_KEYS.LAST_EVENT_SLUG
      );

      if (pendingCid && eventSlug) {
        navigate(`/${eventSlug}/vote/${pendingCid}`, {
          replace: true,
        });
        return;
      }

      if (eventSlug) {
        navigate(`/${eventSlug}/ballot`, {
          replace: true,
        });
        return;
      }

      navigate("/dashboard?facebook", {
        replace: true,
      });

    } catch (err) {
      logger.error("❌ Facebook registration failed", {
        value: err,
      });

      // Existing account with email/password
      if (err.code === "auth/account-exists-with-different-credential") {
        const existingEmail = err.customData?.email || "";

        if (existingEmail) {
          await showExistingAccountWarning(existingEmail);
        } else {
          showStatus(setStatus, {
            type: "warning",
            message:
              "An account already exists with this email. Please log in using your original login method.",
          });
        }

        return;
      }

      showStatus(setStatus, {
        type: "error",
        message: "Facebook registration failed.",
      });
    }
  };

  // ✅ Create or update global user record after SSO registration
  const saveSsoUser = async (user) => {
    const displayNameParts = user.displayName?.split(" ") || [];

    await setDoc(
      doc(db, "users", user.uid),
      {
        uid: user.uid,
        email: user.email || "",
        firstName: displayNameParts[0] || "",
        lastName: displayNameParts.slice(1).join(" ") || "",
        phone: user.phoneNumber || "",
        role: "voter",
        mailingConsent,
        provider: user.providerData?.[0]?.providerId || "sso",
        emailVerified: user.emailVerified || true,
        lastLogin: serverTimestamp(),
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );

    await linkPendingUserToUser({
      email: user.email,
      uid: user.uid,
    });

    logger.log("✅ SSO user saved:", { authenticated: true });
    };

    const getLoginMethodLabel = (methods = []) => {
    if (methods.includes("password")) return "email and password";
    if (methods.includes("google.com")) return "Google";
    if (methods.includes("facebook.com")) return "Facebook";
    return "your original login method";
  };

  const showExistingAccountWarning = async (accountEmail) => {
    const normalizedEmail = accountEmail.trim().toLowerCase();

    const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail);
    const methodLabel = getLoginMethodLabel(methods);

    showStatus(setStatus, {
      type: "warning",
      message: `An account already exists for ${normalizedEmail}. Please log in with ${methodLabel}.`,
    });
  };

  // ✅ Register or login with Google
  const handleGoogleRegister = async () => {
    const provider = new GoogleAuthProvider();
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    try {
      if (isSafari) {
        logger.log("🧭 Safari detected — using Google redirect registration");
        await signInWithRedirect(auth, provider);
        return;
      }

      const result = await signInWithPopup(auth, provider);
      await saveSsoUser(result.user);

      const eventSlug = getSessionValue(
        SESSION_KEYS.LAST_EVENT_SLUG
      );

      const pendingCid = getSessionValue(
        SESSION_KEYS.PENDING_VOTE_CID
      );

      if (pendingCid && eventSlug) {
        removeSessionValue(
          SESSION_KEYS.PENDING_VOTE_CID
        );
        navigate(`/${eventSlug}/vote/${pendingCid}`, { replace: true });
      } else if (eventSlug) {
        navigate(`/${eventSlug}/ballot`, { replace: true });
      } else {
        navigate("/dashboard?googleRegister", { replace: true });
      }

      } catch (err) {
        logger.error("❌ Google registration failed:", { value: err });

        if (err.code === "auth/account-exists-with-different-credential") {
          const existingEmail = err.customData?.email || "";

          if (existingEmail) {
            await showExistingAccountWarning(existingEmail);
          } else {
            showStatus(setStatus, {
              type: "warning",
              message:
                "An account already exists with this email. Please log in using your original login method.",
            });
          }

          return;
        }

        showStatus(setStatus, {
          type: "error",
          message: `Google registration failed: ${err.message}`,
        });
      }

    };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim() || !email.trim() || (!registrationUserRef.current && password.trim().length < 6) || !agreedToTerms) {

      showStatus(setStatus, {
        type: "error",
        message: "Complete all required fields, use at least 6 password characters, and accept the Terms & Conditions.",
      });

      return;
    }

    try {
      // Share profile persistence and verification with inline entry signup.
      // Keep a newly created identity for retries after profile/email failure.
      if (!registrationUserRef.current) {
        registrationUserRef.current = (await createUserWithEmailAndPassword(auth, email.trim(), password)).user;
      }
      await completeEmailRegistration(registrationUserRef.current, {
        firstName, lastName, phone, mailingConsent, agreedToTerms,
      });
      setPassword("");
      trackEvent({
        action: GA_EVENTS.REGISTRATION_SUBMITTED,
        parameters: { method: "email" },
      });

      setTimeout(() => {
        navigate("/dashboard?verifyRequired=1", {
          state: {
            successMsg:
              "Account created! Please check your email and verify your account before voting.",
          },
          replace: true,
        });
      }, 500);

    } catch (err) {
      logger.error("❌ Registration error:", { code: err.code || "unknown" });

      if (err.code === "auth/email-already-in-use") {
        await showExistingAccountWarning(email);
        return;
      }

      showStatus(setStatus, {
        type: "error",
        message: getFriendlyErrorMessage(err.code),
      });

    }
  };
  

  logger.log("🔍 pendingVote data", {
    value: pendingVote
  });

  return (
    <>
    <SeoHead {...PUBLIC_PAGE_SEO.register} />
    <section id="top-content" className="container-fluid">
      <div className="container">
        <div className="row justify-content-center text-center">
          <div className="col-xs-12">
            <h1 className="text-white mb-3">Register to Vote</h1>
            <h4 className="text-white-50 mb-4">
              Just a few steps to start voting. Your info will stay private.
            </h4>
          </div>
  
          <div className="col-xs-12 col-md-8">

            {isVote && (
              <div className="alert alert-warning text-start mt-4 shadow-sm">
                <strong>🗳️ Your vote is saved</strong> for the 
                {pendingVote?.description && (
                  <>
                    {" "} <strong>{pendingVote.description}</strong> in the{" "}
                    <strong>{categoryName || "Unknown"}</strong> category.
                  </>
                )}
                <br />
                Please finish registration below, then confirm your email to complete submission.
              </div>
            )}

            {/* =====================================================
                🔐 Social Registration
            ===================================================== */}

            <div className="text-center mb-4">

              <div className="d-grid gap-2">

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleGoogleRegister}
                >
                  🔐 Continue with Google
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleFacebookRegister}
                >
                  📘 Continue with Facebook
                </button>

              </div>

              <p className="form-text mt-3 text-muted">
                Continue with Google or Facebook for faster registration.
              </p>

              <hr className="my-4" />

              <h5 className="text-center mb-3">
                Or register with email
              </h5>

            </div>

            {/* 🧾 Registration Form */}
            <div className="bg-white p-4 rounded shadow text-start">

              {/* ❌ Error Message */}
              {status?.message && (
                <div
                  className={`alert ${
                    status.type === "success"
                      ? "alert-success"
                      : status.type === "warning"
                      ? "alert-warning"
                      : status.type === "error"
                      ? "alert-danger"
                      : "alert-info"
                  }`}
                >
                  {status.message}
                </div>
              )}
           
              <div className="row mb-3">
                <div className="col-md-6">
                  <label>First Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Jane"
                  />
                </div>
                <div className="col-md-6">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                  />
                </div>
              </div>
  
              <div className="row mb-3">
                <div className="col-md-6">
                  <label>Email *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="col-md-6">
                  <label>Password *</label>
                  <input
                    type="password"
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                  />
                </div>
              </div>
  
              <div className="mb-3">
                <label>Phone (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone number"
                />
              </div>
              <div className="form-check mt-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="mailingConsent"
                  checked={mailingConsent}
                  onChange={() => setMailingConsent(!mailingConsent)}
                />
                <label className="form-check-label" htmlFor="mailingConsent">
                  I'd like to receive event updates and announcements from <strong>Let's Go Vote</strong>.
                </label>
              </div>

              <div className="form-check mb-3 rounded">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="termsCheckbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  required
                />
                <label className="form-check-label" htmlFor="termsCheckbox">
                  * I agree to the <Link to="/terms" target="_blank">Terms & Conditions</Link>
                </label>

              </div>
  
              <div className="d-grid">

                {requiredFieldsCompleteExceptTerms && (
                  <div className="alert alert-warning text-center mb-2 py-2">
                    👆 Please agree to the Terms & Conditions to enable registration.
                  </div>
                )}

                <button
                  type="button"
                  className={`btn ${isFormValid ? "btn-primary" : "btn-secondary"}`}
                  onClick={handleRegister}
                  disabled={!isFormValid}
                >
                  ✅ Register and Start Voting
                </button>
              </div>
            </div>
  
            <div className="mt-3 text-center text-white-50">
              Already have an account?{" "}
              <Link to={`/login${cid ? `?cid=${encodeURIComponent(cid)}` : ""}`}>
                Login here
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}

export default Register;
